import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { Menu, X, Map, Home, BookOpen, TrendingUp, MessageCircle, User, LogOut, Search, SlidersHorizontal, Bed, Bath, Maximize, Users, Building, Shield, FolderOpen, CheckSquare, Languages, Bell, Check, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { AlertsSystem } from './AlertsSystem';
import { motion, AnimatePresence, useMotionValue, useSpring } from 'framer-motion';
import axios from 'axios';

/* ── AntiGravity Logo ─────────────────────────────────── */
const AntiGravityLogo = ({ onClick }) => {
  const letters = 'E-HOME'.split('');
  const containerRef = useRef(null);

  const LetterSpan = ({ char, index }) => {
    const x = useMotionValue(0);
    const y = useMotionValue(0);
    const springX = useSpring(x, { stiffness: 180, damping: 14 });
    const springY = useSpring(y, { stiffness: 180, damping: 14 });
    const spanRef = useRef(null);

    const handleMouseMove = (e) => {
      if (!spanRef.current) return;
      const r = spanRef.current.getBoundingClientRect();
      const cx = r.left + r.width / 2;
      const cy = r.top + r.height / 2;
      const dx = e.clientX - cx;
      const dy = e.clientY - cy;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const radius = 55;
      if (dist < radius) {
        const force = (radius - dist) / radius;
        x.set(-dx * force * 1.4);
        y.set(-dy * force * 1.4);
      } else {
        x.set(0);
        y.set(0);
      }
    };

    const handleMouseLeave = () => { x.set(0); y.set(0); };

    return (
      <motion.span
        ref={spanRef}
        style={{
          x: springX, y: springY,
          display: 'inline-block',
          color: '#c9a84c',
          fontFamily: 'var(--font-display)',
          fontWeight: 700,
          fontSize: '1.375rem',
          letterSpacing: char === '-' ? '-0.02em' : '-0.01em',
          cursor: 'default',
          userSelect: 'none',
        }}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        {char}
      </motion.span>
    );
  };

  return (
    <button onClick={onClick} className="mb-8 text-left block" data-testid="logo" style={{ background: 'none', border: 'none', padding: 0 }}>
      <div ref={containerRef} style={{ lineHeight: 1 }}>
        {letters.map((char, i) => <LetterSpan key={i} char={char} index={i} />)}
      </div>
      <span className="block text-xs mt-1" style={{ color: '#7a7870', fontFamily: 'var(--font-body)' }}>
        Plataforma inmobiliaria
      </span>
    </button>
  );
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* ─── Gold token shorthand ─────────────────────────── */
const G = {
  gold: '#c9a84c',
  goldDim: 'rgba(201,168,76,0.15)',
  goldBorder: 'rgba(201,168,76,0.3)',
  bg: '#050508',
  surface: '#0d0d14',
  text: '#e8e6e0',
  muted: '#7a7870',
  border: 'rgba(255,255,255,0.07)',
};

export const Layout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { theme, themes } = useTheme();
  const { language, toggleLanguage, t } = useLanguage();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [pendingTasks, setPendingTasks] = useState(0);
  const [pendingInvites, setPendingInvites] = useState([]);

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const fetchNotifications = async () => {
      if (!user) return;
      try {
        const [messagesRes, tasksRes, invitesRes] = await Promise.all([
          axios.get(`${API}/messages/unread`),
          axios.get(`${API}/tasks/pending`),
          axios.get(`${API}/organizations/invites/pending`).catch(() => ({ data: [] }))
        ]);
        const newUnreadCount = messagesRes.data.unread_count || 0;
        const newPendingCount = tasksRes.data.pending_count || 0;
        const newInvitesList = Array.isArray(invitesRes.data) ? invitesRes.data : [];
        if (newUnreadCount > unreadMessages && unreadMessages !== 0) playNotificationSound();
        if (newInvitesList.length > pendingInvites.length && pendingInvites.length !== 0) playNotificationSound();
        setUnreadMessages(newUnreadCount);
        setPendingTasks(newPendingCount);
        setPendingInvites(newInvitesList);
      } catch {}
    };
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 5000);
    return () => clearInterval(interval);
  }, [user, unreadMessages, pendingInvites.length]);

  const playNotificationSound = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 800;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.2);
    } catch {}
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const navItems = [
    { path: '/dashboard', label: language === 'es' ? 'Mapa' : 'Map', icon: Map },
    { path: '/publish', label: language === 'es' ? 'Publicar' : 'Publish', icon: Home },
    { path: '/my-properties', label: language === 'es' ? 'Mis Propiedades' : 'My Properties', icon: FolderOpen },
    { path: '/tasks', label: language === 'es' ? 'Mis Tareas' : 'My Tasks', icon: CheckSquare, premiumOnly: true },
    { path: '/knowledge', label: language === 'es' ? 'Conocimiento' : 'Knowledge', icon: BookOpen, essentialPlus: true },
    { path: '/invest', label: language === 'es' ? 'Invertir' : 'Invest', icon: TrendingUp, essentialPlus: true },
    { path: '/crm', label: language === 'es' ? 'Organización' : 'Organization', icon: Building, premiumOnly: true },
    { path: '/community', label: language === 'es' ? 'Comunidad' : 'Community', icon: Users, essentialPlus: true },
    { path: '/messages', label: language === 'es' ? 'Mensajería' : 'Messages', icon: MessageCircle },
    { path: '/profile', label: language === 'es' ? 'Mi Perfil' : 'My Profile', icon: User },
  ];

  const adminNavItem = user?.is_admin ? { path: '/admin', label: 'Admin', icon: Shield } : null;

  const planLabel = (() => {
    const p = user?.subscription_plan;
    if (p === 'profesional' || p === 'premium') return 'PROFESIONAL';
    if (p === 'usuario_avanzado' || p === 'essential') return 'AVANZADO';
    return 'USUARIO';
  })();

  return (
    <div className="min-h-screen flex" style={{ background: G.bg }}>
      {/* Overlay */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9990] lg:hidden"
            style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Toggle Button */}
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="fixed top-16 sm:top-4 left-4 z-[9992] p-2.5 rounded-xl"
        style={{ background: G.surface, border: `1px solid ${G.border}`, color: G.gold }}
        data-testid="toggle-sidebar-btn"
      >
        {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
      </motion.button>

      {/* Sidebar */}
      <motion.aside
        initial={false}
        animate={{ x: sidebarOpen ? 0 : -288 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        className="fixed top-0 left-0 h-screen z-[9991] overflow-y-auto flex flex-col"
        style={{ width: 288, background: G.surface, borderRight: `1px solid ${G.border}` }}
      >
        <div className="p-5 pt-28 sm:pt-[4.5rem] flex flex-col h-full">
          {/* Logo — AntiGravity */}
          <AntiGravityLogo onClick={() => navigate('/dashboard')} />

          {/* Nav */}
          <nav className="space-y-0.5 flex-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              const userPlan = user?.subscription_plan || 'usuario';
              const isPro = userPlan === 'profesional' || userPlan === 'premium';
              const isAvanzado = isPro || userPlan === 'usuario_avanzado' || userPlan === 'essential';
              let badge = null;
              if (item.premiumOnly && !isPro) badge = 'PRO';
              else if (item.essentialPlus && !isAvanzado) badge = 'PRO';
              let notif = 0;
              if (item.path === '/messages') notif = unreadMessages;
              else if (item.path === '/tasks') notif = pendingTasks;
              else if (item.path === '/crm') notif = pendingInvites.length;

              return (
                <motion.button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  whileHover={{ x: isActive ? 0 : 3 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all relative"
                  style={{
                    background: isActive ? G.goldDim : 'transparent',
                    color: isActive ? G.gold : G.muted,
                    border: isActive ? `1px solid ${G.goldBorder}` : '1px solid transparent',
                    fontFamily: 'var(--font-body)',
                  }}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  <Icon size={17} strokeWidth={isActive ? 2 : 1.5} />
                  <span className="flex-1 text-left">{item.label}</span>
                  {badge && (
                    <span className="text-xs font-bold px-1.5 py-0.5 rounded" style={{ background: G.goldDim, color: G.gold, fontSize: '0.6rem' }}>{badge}</span>
                  )}
                  {notif > 0 && !badge && (
                    <span className="text-xs font-bold px-1.5 py-0.5 rounded-full animate-pulse" style={{ background: '#ef4444', color: '#fff', fontSize: '0.6rem' }}>
                      {notif}
                    </span>
                  )}
                </motion.button>
              );
            })}

            {adminNavItem && (
              <motion.button
                onClick={() => navigate(adminNavItem.path)}
                whileHover={{ x: 3 }}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all"
                style={{
                  color: location.pathname === adminNavItem.path ? G.gold : G.muted,
                  background: location.pathname === adminNavItem.path ? G.goldDim : 'transparent',
                  border: location.pathname === adminNavItem.path ? `1px solid ${G.goldBorder}` : '1px solid transparent',
                  fontFamily: 'var(--font-body)',
                }}
              >
                <Shield size={17} strokeWidth={1.5} />
                <span>{adminNavItem.label}</span>
              </motion.button>
            )}
          </nav>

          {/* User card */}
          <div className="mt-auto pt-4" style={{ borderTop: `1px solid ${G.border}` }}>
            {user && (
              <div className="mb-2 p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)' }}>
                <div className="flex items-center gap-3 mb-2">
                  {user.picture ? (
                    <img src={user.picture} alt="" className="w-9 h-9 rounded-full object-cover" style={{ ring: `2px solid ${G.goldBorder}` }} />
                  ) : (
                    <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold"
                      style={{ background: G.goldDim, color: G.gold, border: `1px solid ${G.goldBorder}`, fontFamily: 'var(--font-display)' }}>
                      {user.name?.[0]?.toUpperCase() || 'U'}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate" style={{ color: G.text, fontFamily: 'var(--font-body)' }}>{user.name}</div>
                    <div className="text-xs truncate" style={{ color: G.muted }}>{user.email}</div>
                  </div>
                </div>
                <span className="text-xs font-bold px-2 py-0.5 rounded"
                  style={{ background: G.goldDim, color: G.gold, fontFamily: 'var(--font-body)', letterSpacing: '0.08em', fontSize: '0.6rem' }}>
                  {planLabel}
                </span>
              </div>
            )}

            <motion.button
              whileHover={{ x: 2 }}
              onClick={toggleLanguage}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm mb-1 transition-colors"
              style={{ color: G.muted, fontFamily: 'var(--font-body)' }}
              onMouseEnter={e => e.currentTarget.style.color = G.text}
              onMouseLeave={e => e.currentTarget.style.color = G.muted}
            >
              <Languages size={16} strokeWidth={1.5} />
              <span>{language === 'es' ? 'Español' : 'English'}</span>
            </motion.button>

            <motion.button
              whileHover={{ x: 2 }}
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors"
              style={{ color: G.muted, fontFamily: 'var(--font-body)' }}
              onMouseEnter={e => { e.currentTarget.style.color = '#ef4444'; e.currentTarget.style.background = 'rgba(239,68,68,0.06)'; }}
              onMouseLeave={e => { e.currentTarget.style.color = G.muted; e.currentTarget.style.background = 'transparent'; }}
            >
              <LogOut size={16} strokeWidth={1.5} />
              <span>{language === 'es' ? 'Cerrar sesión' : 'Logout'}</span>
            </motion.button>
          </div>
        </div>
      </motion.aside>

      {/* Main Content — overflow-x:clip not overflow-x:hidden to avoid WebKit bug where fixed children get clipped */}
      <main className="flex-1" style={{ background: G.bg, overflowX: 'clip' }}>
        <div className="w-full">
          <AlertsSystem />
          {children}
        </div>
      </main>
    </div>
  );
};
